import {
  ArrowPathIcon,
  BeakerIcon,
  NoSymbolIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Button, Divider } from '@heroui/react';
import { type FC, useState } from 'react';
import AccountConfirmModal from '@/app/components/account/account-confirm-modal.tsx';
import AccountDeactivatedBadge from '@/app/components/account/account-deactivated-badge.tsx';
import AccountFlagBadge from '@/app/components/account/account-flag-badge.tsx';
import { useSecurityContext } from '@/app/hooks/security-provider.tsx';
import toast from '@/app/toast.ts';
import type { AccountModel } from '@/app/types/account.type.ts';
import { AccountFlagEnum } from '@/app/types/account-flag.type.ts';
import {
  useDeactivateAccountMutation,
  useReactivateAccountMutation,
  useSetAccountFlagMutation,
} from '@/slices/account-api-slice.ts';

type ConfirmAction = 'deactivate' | 'reactivate' | 'markDemo' | 'removeDemo';

interface AccountAdminSettingsProps {
  account: AccountModel;
}

const AccountAdminSettings: FC<AccountAdminSettingsProps> = ({ account }) => {
  const { visibleAccounts } = useSecurityContext();
  const accountMeta = visibleAccounts?.find(item => item.key === account.key);
  const isDeactivated = !!accountMeta?.deactivatedAt;
  const accountFlagId = accountMeta?.accountFlagId ?? account.accountFlagId;
  const isDemo = accountFlagId === AccountFlagEnum.Demo;

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [deactivateAccount, { isLoading: isDeactivating }] = useDeactivateAccountMutation();
  const [reactivateAccount, { isLoading: isReactivating }] = useReactivateAccountMutation();
  const [setAccountFlag, { isLoading: isSettingFlag }] = useSetAccountFlagMutation();

  const isConfirmLoading = isDeactivating || isReactivating || isSettingFlag;

  const closeConfirm = () => setConfirmAction(null);

  const handleConfirm = async () => {
    if (!confirmAction) {
      return;
    }

    try {
      switch (confirmAction) {
        case 'deactivate':
          await deactivateAccount(account.key).unwrap();
          toast.success('Account deactivated.');
          break;
        case 'reactivate':
          await reactivateAccount(account.key).unwrap();
          toast.success('Account reactivated.');
          break;
        case 'markDemo':
          await setAccountFlag({ key: account.key, accountFlagId: AccountFlagEnum.Demo }).unwrap();
          toast.success('Account marked as demo.');
          break;
        case 'removeDemo':
          await setAccountFlag({ key: account.key, accountFlagId: AccountFlagEnum.None }).unwrap();
          toast.success('Demo flag removed.');
          break;
      }
      closeConfirm();
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
  };

  const confirmConfig = {
    deactivate: {
      eyebrow: 'Admin action',
      title: 'Deactivate account',
      confirmLabel: 'Deactivate account',
      tone: 'danger' as const,
      icon: <NoSymbolIcon className='size-5' />,
      description: (
        <>
          <p>
            Deactivate{' '}
            <span className='font-semibold text-zinc-900 dark:text-white'>
              {account.accountName}
            </span>
            ?
          </p>
          <p className='mt-3'>
            This hides availability and meetings for this account. Representatives who are already
            connected can still see it under My Accounts.
          </p>
        </>
      ),
    },
    reactivate: {
      eyebrow: 'Admin action',
      title: 'Reactivate account',
      confirmLabel: 'Reactivate account',
      tone: 'primary' as const,
      icon: <ArrowPathIcon className='size-5' />,
      description: (
        <>
          <p>
            Reactivate{' '}
            <span className='font-semibold text-zinc-900 dark:text-white'>
              {account.accountName}
            </span>
            ?
          </p>
          <p className='mt-3'>Availability and meetings will be visible again for this account.</p>
        </>
      ),
    },
    markDemo: {
      eyebrow: 'Admin action',
      title: 'Mark as demo',
      confirmLabel: 'Mark as demo',
      tone: 'warning' as const,
      icon: <BeakerIcon className='size-5' />,
      description: (
        <>
          <p>
            Mark{' '}
            <span className='font-semibold text-zinc-900 dark:text-white'>
              {account.accountName}
            </span>{' '}
            as a demo account?
          </p>
          <p className='mt-3'>
            It will disappear from representative search. Existing connections, meetings,
            availability, and QR/request links keep working.
          </p>
        </>
      ),
    },
    removeDemo: {
      eyebrow: 'Admin action',
      title: 'Remove demo flag',
      confirmLabel: 'Remove demo flag',
      tone: 'primary' as const,
      icon: <BeakerIcon className='size-5' />,
      description: (
        <>
          <p>
            Remove the demo flag from{' '}
            <span className='font-semibold text-zinc-900 dark:text-white'>
              {account.accountName}
            </span>
            ?
          </p>
          <p className='mt-3'>It will appear in representative search again.</p>
        </>
      ),
    },
  };

  const activeConfirm = confirmAction ? confirmConfig[confirmAction] : null;

  return (
    <>
      <div className='relative mt-12 mb-6 max-w-screen-lg self-center w-full'>
        <Divider />
        <span className='absolute left-0 top-0 -translate-y-1/2 bg-white pr-2 text-sm text-primary-500 font-semibold uppercase dark:bg-zinc-950'>
          Admin Controls
        </span>
      </div>

      <div className='flex flex-col gap-4 max-w-screen-lg self-center w-full rounded-2xl border border-zinc-200 bg-zinc-50/80 p-5 dark:border-zinc-800 dark:bg-zinc-900/40'>
        <div className='flex items-start gap-3'>
          <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'>
            <ShieldExclamationIcon className='size-5' />
          </span>
          <div className='flex flex-col gap-1'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-semibold text-zinc-900 dark:text-white'>
                Super admin only
              </span>
              <AccountFlagBadge accountFlagId={accountFlagId} />
              <AccountDeactivatedBadge deactivatedAt={accountMeta?.deactivatedAt} />
            </div>
            <p className='text-sm text-zinc-600 dark:text-zinc-400'>
              Demo and deactivation settings affect search visibility and account availability.
            </p>
          </div>
        </div>

        <div className='flex flex-wrap gap-2'>
          <Button
            variant='bordered'
            color='warning'
            onPress={() => setConfirmAction(isDemo ? 'removeDemo' : 'markDemo')}
          >
            {isDemo ? 'Remove Demo Flag' : 'Mark as Demo'}
          </Button>
          {isDeactivated ? (
            <Button
              variant='bordered'
              color='primary'
              onPress={() => setConfirmAction('reactivate')}
            >
              Reactivate Account
            </Button>
          ) : (
            <Button
              variant='bordered'
              color='danger'
              onPress={() => setConfirmAction('deactivate')}
            >
              Deactivate Account
            </Button>
          )}
        </div>
      </div>

      {activeConfirm && (
        <AccountConfirmModal
          isOpen={!!confirmAction}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
          isLoading={isConfirmLoading}
          eyebrow={activeConfirm.eyebrow}
          title={activeConfirm.title}
          description={activeConfirm.description}
          confirmLabel={activeConfirm.confirmLabel}
          tone={activeConfirm.tone}
          icon={activeConfirm.icon}
        />
      )}
    </>
  );
};

export default AccountAdminSettings;
